/**
 * @fileoverview ClubOS Role-Based Access Control — roles and permission matrix
 */

/** Canonical role names */
export const ROLES = {
  CHAIRMAN:            "Chairman",
  CEO:                 "CEO",
  CFO:                 "CFO",
  DIRECTOR_FOOTBALL:   "Director of Football",
  HEAD_COACH:          "Head Coach",
  ASSISTANT_COACH:     "Assistant Coach",
  CHIEF_SCOUT:         "Chief Scout",
  SCOUT:               "Scout",
  TEAM_DOCTOR:         "Team Doctor",
  CLUB_SECRETARY:      "Club Secretary",
  FINANCE_EXECUTIVE:   "Finance Executive",
  OPERATIONS_MANAGER:  "Operations Manager",
  MEDIA_MANAGER:       "Media Manager",
  SYSTEM_ADMIN:        "System Administrator",
};

/** Fine-grained permission strings */
export const PERMISSIONS = {
  // Finance
  FINANCE_VIEW:         "finance.view",
  FINANCE_EDIT:         "finance.edit",
  FINANCE_ADMIN:        "finance.admin",
  SALARY_VIEW:          "finance.salary.view",       // SENSITIVE
  SALARY_EDIT:          "finance.salary.edit",
  TRANSFER_VIEW:        "finance.transfer.view",
  TRANSFER_EDIT:        "finance.transfer.edit",
  TRANSFER_APPROVE:     "finance.transfer.approve",
  BUDGET_VIEW:          "finance.budget.view",
  BUDGET_EDIT:          "finance.budget.edit",
  REVENUE_VIEW:         "finance.revenue.view",
  REVENUE_EDIT:         "finance.revenue.edit",

  // Squad
  SQUAD_VIEW:           "squad.view",
  SQUAD_EDIT:           "squad.edit",
  SQUAD_ADMIN:          "squad.admin",

  // Coaching
  COACHING_VIEW:        "coaching.view",
  COACHING_EDIT:        "coaching.edit",
  COACHING_ADMIN:       "coaching.admin",

  // Scouting
  SCOUTING_VIEW:        "scouting.view",
  SCOUTING_EDIT:        "scouting.edit",
  SCOUTING_ADMIN:       "scouting.admin",

  // Users
  USERS_VIEW:           "users.view",
  USERS_EDIT:           "users.edit",
  USERS_ADMIN:          "users.admin",

  // Reports
  REPORTS_VIEW:         "reports.view",
  REPORTS_EXPORT:       "reports.export",
};

const ALL = Object.values(PERMISSIONS);

/** Permission matrix — maps each role to its allowed permissions */
export const ROLE_PERMISSIONS = {
  [ROLES.CHAIRMAN]: ALL,
  [ROLES.CEO]:      ALL,

  [ROLES.CFO]: [
    PERMISSIONS.FINANCE_VIEW,   PERMISSIONS.FINANCE_EDIT,  PERMISSIONS.FINANCE_ADMIN,
    PERMISSIONS.SALARY_VIEW,    PERMISSIONS.SALARY_EDIT,
    PERMISSIONS.TRANSFER_VIEW,  PERMISSIONS.TRANSFER_EDIT, PERMISSIONS.TRANSFER_APPROVE,
    PERMISSIONS.BUDGET_VIEW,    PERMISSIONS.BUDGET_EDIT,
    PERMISSIONS.REVENUE_VIEW,   PERMISSIONS.REVENUE_EDIT,
    PERMISSIONS.SQUAD_VIEW,
    PERMISSIONS.USERS_VIEW,
    PERMISSIONS.REPORTS_VIEW,   PERMISSIONS.REPORTS_EXPORT,
  ],

  [ROLES.DIRECTOR_FOOTBALL]: [
    PERMISSIONS.FINANCE_VIEW,   PERMISSIONS.TRANSFER_VIEW,  PERMISSIONS.TRANSFER_APPROVE,
    PERMISSIONS.BUDGET_VIEW,    PERMISSIONS.REVENUE_VIEW,
    PERMISSIONS.SQUAD_VIEW,     PERMISSIONS.SQUAD_EDIT,     PERMISSIONS.SQUAD_ADMIN,
    PERMISSIONS.COACHING_VIEW,
    PERMISSIONS.SCOUTING_VIEW,  PERMISSIONS.SCOUTING_EDIT,  PERMISSIONS.SCOUTING_ADMIN,
    PERMISSIONS.USERS_VIEW,
    PERMISSIONS.REPORTS_VIEW,   PERMISSIONS.REPORTS_EXPORT,
  ],

  [ROLES.HEAD_COACH]: [
    PERMISSIONS.SQUAD_VIEW,     PERMISSIONS.SQUAD_EDIT,
    PERMISSIONS.COACHING_VIEW,  PERMISSIONS.COACHING_EDIT,  PERMISSIONS.COACHING_ADMIN,
    PERMISSIONS.SCOUTING_VIEW,
    PERMISSIONS.REPORTS_VIEW,
  ],

  [ROLES.ASSISTANT_COACH]: [
    PERMISSIONS.SQUAD_VIEW,
    PERMISSIONS.COACHING_VIEW,  PERMISSIONS.COACHING_EDIT,
    PERMISSIONS.REPORTS_VIEW,
  ],

  [ROLES.CHIEF_SCOUT]: [
    PERMISSIONS.SQUAD_VIEW,
    PERMISSIONS.SCOUTING_VIEW,  PERMISSIONS.SCOUTING_EDIT,  PERMISSIONS.SCOUTING_ADMIN,
    PERMISSIONS.TRANSFER_VIEW,
    PERMISSIONS.REPORTS_VIEW,   PERMISSIONS.REPORTS_EXPORT,
  ],

  [ROLES.SCOUT]: [
    PERMISSIONS.SQUAD_VIEW,
    PERMISSIONS.SCOUTING_VIEW,  PERMISSIONS.SCOUTING_EDIT,
  ],

  [ROLES.TEAM_DOCTOR]: [
    PERMISSIONS.SQUAD_VIEW,
    PERMISSIONS.REPORTS_VIEW,
  ],

  [ROLES.CLUB_SECRETARY]: [
    PERMISSIONS.SQUAD_VIEW,     PERMISSIONS.SQUAD_EDIT,
    PERMISSIONS.FINANCE_VIEW,   PERMISSIONS.TRANSFER_VIEW,
    PERMISSIONS.REPORTS_VIEW,
  ],

  [ROLES.FINANCE_EXECUTIVE]: [
    PERMISSIONS.FINANCE_VIEW,   PERMISSIONS.FINANCE_EDIT,
    PERMISSIONS.BUDGET_VIEW,    PERMISSIONS.REVENUE_VIEW,   PERMISSIONS.REVENUE_EDIT,
    PERMISSIONS.TRANSFER_VIEW,
    PERMISSIONS.REPORTS_VIEW,
  ],

  [ROLES.OPERATIONS_MANAGER]: [
    PERMISSIONS.SQUAD_VIEW,
    PERMISSIONS.COACHING_VIEW,
    PERMISSIONS.REPORTS_VIEW,
  ],

  [ROLES.MEDIA_MANAGER]: [
    PERMISSIONS.SQUAD_VIEW,
    PERMISSIONS.REPORTS_VIEW,
  ],

  [ROLES.SYSTEM_ADMIN]: ALL,
};

/** Human-readable role descriptions for the UI */
export const ROLE_META = {
  [ROLES.CHAIRMAN]:           { color: "dark",      icon: "stars",                description: "Full access to all modules, all data" },
  [ROLES.CEO]:                { color: "dark",      icon: "business_center",       description: "Full operational access across the platform" },
  [ROLES.CFO]:                { color: "error",     icon: "account_balance",       description: "Finance admin, squad view, salary access" },
  [ROLES.DIRECTOR_FOOTBALL]:  { color: "primary",   icon: "sports_soccer",         description: "Squad, Scouting, Transfer admin; Finance view" },
  [ROLES.HEAD_COACH]:         { color: "info",      icon: "manage_accounts",       description: "Coaching admin, Squad edit, Scouting view" },
  [ROLES.ASSISTANT_COACH]:    { color: "info",      icon: "supervisor_account",    description: "Coaching edit, Squad view" },
  [ROLES.CHIEF_SCOUT]:        { color: "warning",   icon: "search",                description: "Scouting admin, squad view, transfer view" },
  [ROLES.SCOUT]:              { color: "warning",   icon: "find_in_page",          description: "Scouting edit (own reports only)" },
  [ROLES.TEAM_DOCTOR]:        { color: "success",   icon: "medical_services",      description: "Squad availability view, medical records" },
  [ROLES.CLUB_SECRETARY]:     { color: "secondary", icon: "description",           description: "Squad edit, contracts view, compliance" },
  [ROLES.FINANCE_EXECUTIVE]:  { color: "error",     icon: "payments",              description: "Finance edit (no salary), revenue, budget" },
  [ROLES.OPERATIONS_MANAGER]: { color: "secondary", icon: "settings",              description: "Operational visibility across modules" },
  [ROLES.MEDIA_MANAGER]:      { color: "secondary", icon: "campaign",              description: "Squad view, public info only" },
  [ROLES.SYSTEM_ADMIN]:       { color: "dark",      icon: "admin_panel_settings",  description: "Platform administration, user management" },
};
